import type { Message, Topic } from "@mythic/coms/types";
import {
    createContext,
    useContext,
    useState,
    useEffect,
    useRef,
    type ReactNode,
} from "react";

interface WebSocketContextType {
    isConnected: boolean;
    isConnecting: boolean;
    isAuthorized: boolean;
    isGuest: boolean;
    token: string;
    subscribe: (listener: Listener, topics: Topic[]) => () => void;
    sendString: (str: string) => void;
    sendJson: (data: Message) => void;
}

export const useWebSocket = () => {
    const ctx = useContext(WebsocketContext);
    if (!ctx)
        throw new Error("useWebsocket must be used inside a WebsocketProvider");
    return ctx;
};

const WebsocketContext = createContext<WebSocketContextType | null>(null);

interface Props {
    url: string;
    children: ReactNode;
    reconnectInterval?: number;
    maxReconnectAttempts?: number;
    urlParams?: { [key: string]: string };
    onAuthorized?: () => void;
    onLoginRequired?: () => void;
}

type Listener = (data: Message) => void;

const params = new URLSearchParams(window.location.search);
const guest = params.get("guest") === "true";

export function WebsocketProvider({
    url,
    children,
    reconnectInterval = 3000,
    maxReconnectAttempts = 3,
    urlParams = {},
    onAuthorized,
    onLoginRequired,
}: Props) {
    const wsRef = useRef<WebSocket | null>(null);

    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);

    const [isAuthorized, setIsAuthorized] = useState(false);

    const reconnectAttemptsRef = useRef(0);
    const reconnectTimeoutRef = useRef<number | null>(null);
    const topicRegistry = useRef<Map<Topic, Set<Listener>>>(new Map());

    const connect = () => {
        // Clean up old socket.
        wsRef.current?.close();

        // Turn the params into a search query.
        const query = new URLSearchParams(urlParams).toString();
        const qualifiedUrl = `${url}${query.length > 0 ? "?" : ""}${query}`;

        console.log("Connecting to:", qualifiedUrl);
        try {
            setIsConnecting(true);
            wsRef.current = new WebSocket(qualifiedUrl);

            wsRef.current.onopen = () => {
                reconnectAttemptsRef.current = 0;
                setIsConnected(true);
                setIsConnecting(false);
                console.log("[WS] Connection established.");

                if (topicRegistry.current.size > 0)
                    sendSubscribeRequest([...topicRegistry.current.keys()]);
            };

            wsRef.current.onmessage = (ev) => {
                try {
                    const raw = JSON.parse(ev.data);

                    if (
                        typeof raw !== "object" ||
                        raw === null ||
                        !("target" in raw)
                    ) {
                        console.warn("Malformed websocket packet:", raw);
                        return;
                    }

                    const message = raw as Message;

                    console.log("[WS] Received:", message);

                    // If the target is the socket
                    if (message.target === "socket") {
                        // If the action is to authorize
                        if (message.action === "authstatus") {
                            const authMessage = message;
                            // If the authorization is successful
                            if (authMessage.ok) {
                                // Set authorized.
                                setIsAuthorized(true);

                                // Execute onAuthorized to fulfill any
                                // extra requirements.
                                if (onAuthorized) onAuthorized();
                            } else {
                                // Login is required.
                                if (onLoginRequired) onLoginRequired();
                            }
                        }
                    }

                    // The target is not the socket or toast so the message
                    // needs to be passed to listeners to be consumed.
                    else {
                        const topicListeners = topicRegistry.current.get(
                            message.target,
                        );
                        if (topicListeners)
                            topicListeners.forEach((l) => l(message));
                    }
                } catch (err) {
                    console.warn("[WS] Failed to parse message:", ev.data);
                    console.error(err);
                }
            };

            wsRef.current.onerror = (ev) => {
                console.error("[WS] Error:", ev);
                wsRef.current?.close();
            };

            wsRef.current.onclose = (ev) => {
                setIsConnected(false);
                setIsConnecting(false);

                console.log("Socket is closed. Reconnecting.", ev.reason);

                if (reconnectAttemptsRef.current < maxReconnectAttempts) {
                    scheduleReconnect();
                }
            };
        } catch (err) {
            console.log("Error connecting socket:", err);
        }
    };

    const scheduleReconnect = () => {
        if (reconnectTimeoutRef.current !== null) return; // Already scheduled
        reconnectAttemptsRef.current += 1;

        // Create a timeout for reconnection
        reconnectTimeoutRef.current = window.setTimeout(() => {
            // Get rid of the timeout
            reconnectTimeoutRef.current = null;

            // Log and connect.
            console.log(
                `[WS] Reconnect attempt ${reconnectAttemptsRef.current}`,
            );
            connect();
        }, reconnectInterval);
    };

    const subscribe = (listener: Listener, topics: Topic[]) => {
        const newTopics: Topic[] = [];

        topics.forEach((topic) => {
            if (!topicRegistry.current.has(topic)) {
                topicRegistry.current.set(topic, new Set());

                topicRegistry.current.get(topic)!.add(listener);

                newTopics.push(topic);
            }
        });

        if (wsRef.current?.readyState === WebSocket.OPEN)
            sendSubscribeRequest(topics);

        return () => {
            //listeners.current = listeners.current.filter((l) => l !== listener);
            topics.forEach((topic) => {
                const listenersForTopic = topicRegistry.current.get(topic);

                if (listenersForTopic) {
                    listenersForTopic.delete(listener);

                    if (listenersForTopic.size === 0) {
                        topicRegistry.current.delete(topic);
                        if (wsRef.current?.readyState === WebSocket.OPEN)
                            sendUnsubscribeRequest([topic]);
                    }
                }
            });
        };
    };

    const sendString = (str: string) => {
        if (guest) return;
        // Get the socket
        const socket = wsRef.current;

        // Send the string if the socket is open
        if (socket && socket.readyState === WebSocket.OPEN) socket.send(str);
        // Warn if the socket is not open.
        else console.warn("[WS] Tried to send string but socket is not open.");
    };

    const sendJson = (data: Message) => {
        if (guest) return;
        sendString(JSON.stringify({ ...data, token: urlParams.token }));
        console.log("Sending", data);
    };

    const sendSubscribeRequest = (topics: Topic | Topic[]) => {
        if (Array.isArray(topics))
            sendJson({
                target: "socket",
                action: "subscribe",
                topics,
            });
        else
            sendJson({
                target: "socket",
                action: "subscribe",
                topic: topics,
            });
    };

    const sendUnsubscribeRequest = (topics: Topic | Topic[]) => {
        if (Array.isArray(topics))
            sendJson({
                target: "socket",
                action: "unsubscribe",
                topics,
            });
        else
            sendJson({
                target: "socket",
                action: "unsubscribe",
                topic: topics,
            });
    };

    useEffect(() => {
        //if (!("token" in urlParams)) return;
        if (wsRef.current) return;

        connect();

        return () => {
            if (wsRef.current) {
                wsRef.current.close();
            }

            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
        };
    }, [url, urlParams.token]);

    return (
        <WebsocketContext.Provider
            value={{
                isConnected,
                isConnecting,
                isAuthorized,
                isGuest: guest,
                token: urlParams.token,
                subscribe,
                sendString,
                sendJson,
            }}
        >
            {isAuthorized && children}
            {!isAuthorized && <div>Unauthorized</div>}
        </WebsocketContext.Provider>
    );
}
